import { generateRandomUUID } from "../helpers";

class CreateMessageRcs {
  constructor(data) {
    this.data = data || {};
    this._postbackIndex = 0;

    this.mapActions = {
      openUrlAction: "url",
      openUrlActionShortLink: "url",
      ViewLocationAction: "viewLocation",
      dialAction: "dial",
      createCalendarEventAction: "calendar",
      ShareLocationAction: "shareLocation",
    };

    this.deleteFields = [
      "step",
      "title_rcs_message",
      "buttonActions",
      "buttonQuickReplies",
      "fieldsInfo",
      "listDatabases",
      "listShortLinks",
      "listMask",
      "campaignName",
      "fallback",
      "template_fallback",
      "height",
      "imageUrl",
      "carrousel",
      "thumbnail_url",
      "ttl",
      "emoji",
    ];

    this.deleteFieldsMultiChannel = [
      "whmessage",
      "whtemplate",
      "whparameters",
      "email_message",
      "email_subject",
      "email_template",
      "parameters",
      ...this.deleteFields,
    ];
  }

  nextIndex() {
    return this._postbackIndex++;
  }

  formatEmoji(message) {
    if (!message) return "";

    const cleanMessage = this.replaceEmoji(message);
    return cleanMessage.replace(/{{(.*?)}}/g, (_, hex) => ` U+${hex} `);
  }

  replaceEmoji(message) {
    const emoji = this.data?.emoji;

    if (!emoji?.emoji || !emoji?.unified) return message;

    return message.replace(emoji.emoji, `{{${emoji?.unified.toUpperCase()}}}`);
  }

  postback_id(text = null) {
    const uuid = this.getUuidCampaign();
    const index = this.nextIndex();
    if (!text) {
      return `${uuid}_${index + 1}`;
    }

    return `${uuid}_${text.toLowerCase().replace(/\s+/g, "_")}_${index + 1}`;
  }

  getUuidCampaign() {
    return this.data.uuid_campaign
      ? this.data.uuid_campaign
      : generateRandomUUID();
  }

  actions(data) {
    const actions = data || [];

    const newActions = actions
      .filter((item) => item.text)
      .map((item) => {
        return {
          ...item,
          postback: this.postback_id(),
          type: this.mapActions[item.type],
          actions: item.action ?? "",
        };
      });

    return newActions;
  }

  suggestions(data) {
    const suggestions = data || [];

    const newSuggestions = suggestions
      .filter((item) => item.text)
      .map((item) => {
        return {
          ...item,
          postback: this.postback_id(item.text),
        };
      });

    return newSuggestions;
  }

  base() {
    const isScheduled = this.data.isScheduled || false;

    return {
      name: this.data.campaignName,
      hour: isScheduled ? this.data.proghour.split(":")[0] : 0,
      minute: isScheduled ? this.data.proghour.split(":")[1] : 0,
      send: this.data.dateSend,
      isRcs: true,
      idshortcodemask: this.data.idshortcodemask ?? null,
      message: this.formatEmoji(this.data.rcsmessage), //this.data.rcsmessage,
      rcsmessage: this.formatEmoji(this.data.rcsmessage),
    };
  }

  single() {
    const base = this.base();
    const { data } = this;
    const actions = this.actions(data.buttonActions);
    const suggestions = this.suggestions(data.buttonQuickReplies);
    let newData = {
      ...data,
      ...base,
      jDetails: JSON.stringify({
        type: "text",
        fallback: this.data.fallback,
        actions: actions.concat(suggestions),
        thumbnail_url: this.data.thumbnail_url,
        image: this.data.imageUrl,
        height: this.data.height,
        ttl: this.data.ttl,
      }),
    };

    this.deleteFields.forEach((field) => {
      delete newData[field];
    });

    return newData;
  }

  rich() {
    const base = this.base();
    const { data } = this;
    const actions = this.actions(data.buttonActions);
    const suggestions = this.suggestions(data.buttonQuickReplies);

    let newData = {
      ...data,
      ...base,
      jDetails: JSON.stringify({
        type: "richCard",
        fallback: this.data.fallback,
        actions: actions.concat(suggestions),
        text: this.formatEmoji(this.data.title_rcs_message), // en telcel tittle es text
        cardOrientation: this.data.cardOrientation || "VERTICAL",
        image: this.data.imageUrl,
        thumbnail_url: this.data.thumbnail_url,
        height: this.data.height,
        subtitle:
          this.formatEmoji(this.data.rcsmessage) ??
          this.formatEmoji(this.data?.message) ??
          null, // subtitle es message en telcel
        ttl: this.data.ttl,
      }),
    };

    this.deleteFields.forEach((field) => {
      delete newData[field];
    });

    return newData;
  }

  carrousel() {
    const base = this.base();
    const { data } = this;
    let newData = {
      ...base,
      ...data,
      message: this.data.fallback,
      rcsmessage: this.formatEmoji(this.data.fallback),
      jDetails: JSON.stringify({
        type: "carouselCards",
        cardWith: "MEDIUM",
        fallback: this.data.fallback,
        cards: this.data.carrousel.map(
          ({
            imageUrl,
            title,
            buttonActions,
            buttonQuickReplies,
            description,
            thumbnail_url,
            ...remain
          }) => {
            const actions = this.actions(buttonActions);

            const suggestions = this.suggestions(buttonQuickReplies);
            return {
              ...remain,
              title: this.formatEmoji(title),
              description: this.formatEmoji(description),
              image: imageUrl,
              thumbnail_url: thumbnail_url,
              actions: actions.concat(suggestions),
            };
          },
        ),
        ttl: this.data.ttl,
      }),
    };

    this.deleteFields.forEach((field) => {
      delete newData[field];
    });

    return newData;
  }

  buildData() {
    const step = this.data.step || 1;

    switch (step) {
      case 1:
        return this.single();
      case 2:
        return this.rich();
      case 3:
        return this.carrousel();
      default:
        return null;
    }
  }

  multichannel() {
    let newData = {
      partner: this.data.partner,
      uidUser: this.data.uidUser,
      uuid_campaign: this.data.uuid_campaign,
      uiddatabase: this.data.uiddatabase,
      name: this.data.campaignName,
      hour: this.data.isScheduled ? this.data.proghour.split(":")[0] : 0,
      minute: this.data.isScheduled ? this.data.proghour.split(":")[1] : 0,
      send: this.data.dateSend,
      mask: this.data.mask,
      idshortcodemask: this.data.idshortcode > 0 ? this.data.idshortcode : null,
      isRcs: this.data.isRcs,
      isWhatsapp: this.data.isWhatsapp,
      isSms: this.data.isSms,
      isEmail: this.data.isEmail,
      fallback: this.data.fallback,
    };

    let jsonDetails = {};

    if (this.data.isRcs) {
      const rcsbuild = this.buildData();
      const rcsJDetail = JSON.parse(rcsbuild?.jDetails || "{}");

      jsonDetails = {
        ...jsonDetails,
        ...rcsJDetail,
        fallback: this.data.fallback,
        ttl: this.data.ttl,
      };
      newData = {
        ...newData,
        message: rcsbuild.message,
        rcsmessage:
          this.data.step === 3
            ? this.formatEmoji(this.data.message)
            : this.formatEmoji(rcsbuild.rcsmessage),
      };
    }

    if (this.data.isWhatsapp) {
      jsonDetails = {
        ...jsonDetails,
        parameters: this.data.whparameters + ",",
        template: this.data.selectedTemplate,
      };

      newData = {
        ...newData,
      };
    }

    if (this.data.isEmail) {
      newData = {
        ...newData,
        emailmessage: this.data.email_message,
        subject: this.data.email_subject,
      };
    }

    if (this.data.isSms) {
      newData = {
        ...newData,
        message: this.data.message,
      };
    }

    this.deleteFieldsMultiChannel.forEach((field) => {
      delete newData[field];
    });

    return {
      ...newData,
      jDetails: JSON.stringify(jsonDetails),
    };
  }
}

export default CreateMessageRcs;
